import { Link } from "react-router-dom";
import { useCurrency } from "@/context/CurrencyContext";
import { Card } from "@/components/ui/card";
import { MapPin, GraduationCap, ShieldCheck } from "lucide-react";
import { resolveOfferRate, offerRateMetricLabel } from "@/lib/offerRate";
import { rankLabel } from "@/lib/rankLabel";
import SalarySourceBadge from "@/components/app/SalarySourceBadge";

const FLAGS = { UK: "🇬🇧", US: "🇺🇸", Germany: "🇩🇪", France: "🇫🇷" };

export default function UniCard({ uni, chance, feeStatus }) {
  const { format } = useCurrency();
  // Fee-status aware per-year cost — if the caller resolved the student
  // as home-fee eligible for this country, swap the intl tuition out for
  // the domestic rate before summing. Everything else (accommodation,
  // insurance etc.) is nationality-blind.
  const isHome = feeStatus?.status === "home";
  const tuition = isHome
    ? (uni.tuition_domestic_annual_usd || feeStatus?.home_fee_fallback_usd || uni.tuition_intl_annual_usd || 0)
    : (uni.tuition_intl_annual_usd || 0);
  const totalAnnual = tuition
    + (uni.accommodation_annual_usd || 0)
    + (uni.food_annual_usd || 0)
    + (uni.health_insurance_annual_usd || 0)
    + (uni.books_annual_usd || 0)
    + (uni.travel_home_annual_usd || 0)
    + (uni.visa_annual_usd || 0);

  return (
    <Link to={`/uni/${uni.id}`} data-testid={`uni-card-${uni.id}`} className="block h-full">
      <Card className="lift group h-full overflow-hidden border-stone-200 bg-white rounded-lg">
        <div className="aspect-[16/9] overflow-hidden bg-stone-100 relative">
          <img
            src={uni.photo_url || uni.image_url}
            alt={uni.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            data-testid={`uni-card-image-${uni.id}`}
            onError={(e) => { if (uni.image_url && e.target.src !== uni.image_url) e.target.src = uni.image_url; }}
          />
          {uni.photo_url && (
            <div className="absolute bottom-1.5 right-1.5 text-[9px] font-underground-sc tracking-wider text-white/85 bg-black/40 backdrop-blur-sm rounded px-1 py-0.5">
              Image · Google
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center justify-between text-xs">
            <span className="label-uppercase">
              {FLAGS[uni.country] || "🎓"} {uni.country}
              {rankLabel(uni) ? ` · ${rankLabel(uni)}` : ""}
            </span>
            {chance ? (
              <span className={`chance-${chance.classification.bucket} rounded-full px-2 py-0.5 font-mono text-[10px] uppercase`}>
                {chance.classification.label}
              </span>
            ) : feeStatus?.status ? (
              <span
                data-testid={`uni-card-fee-pill-${uni.id}`}
                title={feeStatus.reason || (isHome ? "You qualify for the home fee rate here" : "International rate applies")}
                className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider border ${isHome ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-stone-100 text-stone-700 border-stone-200"}`}
              >
                {isHome ? "Home fee" : "Intl fee"}
              </span>
            ) : uni.confidence === "auto_skeleton" ? (
              <span
                data-testid={`uni-confidence-badge-${uni.id}`}
                title="Outcomes verified via HESA / Discover Uni; editorial review pending."
                className="rounded-full px-2 py-0.5 font-mono text-[10px] uppercase bg-amber-50 text-amber-800 border border-amber-200"
              >
                Data-only
              </span>
            ) : null}
          </div>
          <h3 className="font-editorial text-xl mt-2 leading-snug">{uni.name}</h3>
          <div className="flex items-center gap-2 text-xs text-stone-500 mt-1">
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {uni.city}
            </span>
            {typeof uni?.offer_stats?.offer_rate === "number" && (
              <span
                data-testid={`uni-card-verified-tick-${uni.id}`}
                title={`Offer rate verified via ${uni.offer_stats.cycle_year ? `UCAS/uni cycle ${uni.offer_stats.cycle_year}` : "uni admissions page"}${uni.offer_stats.notes ? ` · ${uni.offer_stats.notes}` : ""}`}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[9px] font-underground-sc tracking-wider px-1.5 py-[1px]"
              >
                <ShieldCheck className="h-2.5 w-2.5" />
                Verified
              </span>
            )}
          </div>
          <p className="text-sm text-stone-600 mt-3 line-clamp-2">{uni.tagline}</p>

          <div className="mt-4 pt-3 border-t border-stone-100 grid grid-cols-3 gap-2 text-xs">
            <div>
              {(() => {
                const or = resolveOfferRate({ uni });
                const metric = offerRateMetricLabel(or);
                if (!or) {
                  return (
                    <>
                      <div className="label-uppercase text-[9px]">Offer rate</div>
                      <div className="font-mono font-semibold">—</div>
                    </>
                  );
                }
                const isStub = or.source === "legacy_editorial_stub";
                return (
                  <>
                    <div className="label-uppercase text-[9px]">{metric}</div>
                    <div
                      data-testid={`uni-card-offer-rate-${uni.id}`}
                      title={or.label + (isStub ? " · seed estimate awaiting verification" : "")}
                      className={`font-mono font-semibold ${isStub ? "text-stone-400" : ""}`}
                    >
                      {`${(or.value * 100).toFixed(1)}%`}
                      {isStub && (
                        <span className="ml-1 text-[8px] uppercase tracking-wider text-amber-700">est</span>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
            <div>
              <div className="label-uppercase text-[9px]">{isHome ? "Cost/yr" : "Intl. cost/yr"}</div>
              <div className="font-mono font-semibold">{format(totalAnnual)}</div>
            </div>
            <div>
              <div className="label-uppercase text-[9px]">Grad. salary</div>
              <div className="font-mono font-semibold" data-testid={`uni-card-salary-${uni.id}`}>{uni.avg_starting_salary_usd != null ? format(uni.avg_starting_salary_usd) : "—"}</div>
              <SalarySourceBadge
                salarySource={uni.outcomes?.salary_source || uni.salary_source}
                salaryIngestedAt={uni.outcomes?.salary_ingested_at}
                size="xs"
                link={false}
                className="mt-0.5"
              />
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}
